import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Circles } from 'react-loader-spinner';
import '../css/JobSeriesScreen.css';
import jobSeriesDictionary from './jobSeriesDictionary';
import updateTokenUsage from './updateTokenUsage';
import { AI_FUNCTIONS_BASE_URL } from '../config/api';

const JobSeriesScreen = ({ formData, setFormData, setTokenUsage, setUsagePercentage, tokenThreshold }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const educationEntries = formData.educationHistory || [];

  useEffect(() => {
    if (formData.selectedJobSeries && formData.selectedJobSeries.length > 0) {
      const loadedRecommendations = formData.selectedJobSeries.map(series => ({
        code: series.code,
        title: series.title,
      }));
      setRecommendations(loadedRecommendations);
    }
  }, [formData.selectedJobSeries]);



  const handleSelectJobSeries = (code, title) => {
    setFormData((prevFormData) => {
        const currentSelection = prevFormData.selectedJobSeries;

        // If the item is already selected, deselect it
        if (currentSelection && currentSelection.code === code) {
            return { ...prevFormData, selectedJobSeries: null };
        }

        // Otherwise, select the new item
        return { ...prevFormData, selectedJobSeries: { code, title } };
    });
};



  const getRecommendations = async () => {
    setIsLoading(true);
    try {
      const allRecommendations = [];
      for (const area of areasOfStudy) {
        const data = JSON.stringify({
          prompt: "jobSeriesSearch",
          collegeMajor: area,
          userID: formData.user
        });

        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: `${AI_FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
          headers: { 
            'Content-Type': 'text/plain, application/json'
          },
          data: data
        };

        const response = await axios.request(config);
        const responseData = response.data;
        const regex = /\b\d{4}\b/g;
        const suggestedSeriesCodes = responseData.match(regex);
        const validRecommendations = suggestedSeriesCodes
          ? suggestedSeriesCodes.filter(code => jobSeriesDictionary[code])
          : [];

        if (validRecommendations.length > 0) {
          allRecommendations.push(...validRecommendations);
        }

        await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);

      }

      if (allRecommendations.length === 0) {
        setError('No valid job series codes found in the response.');
        setRecommendations(null);
      } else {
        const uniqueRecommendations = [...new Set(allRecommendations)].map(code => ({
          code,
          title: jobSeriesDictionary[code]
        }));
        setRecommendations(uniqueRecommendations);
        setError(null);
      }

    } catch (error) {
      setError('Failed to fetch recommendations. Please try again later.');
      setRecommendations(null);
    } finally {
      setIsLoading(false);
    }
  };

  const areasOfStudy = educationEntries.flatMap(entry => {
    const studies = [];
    if (entry.major) studies.push(entry.major);
    if (entry.minor) studies.push(entry.minor);
    return studies;
  });

  const relevantCourses = educationEntries.flatMap(entry => entry.relevantCourses || []);

  if (educationEntries.length === 0) {
    return <p>Please go to the form and enter at least one education history entry.</p>;
  }

  if (areasOfStudy.length === 0) {
    return <p>Please go to the form and enter your major information.</p>;
  }

  if (relevantCourses.length === 0) {
    return <p>Please go to the form and enter relevant courses.</p>;
  }

  return (
    <div className="job-series-screen">
      <h1>Determine Job Series</h1>
      <div className="job-series-screen responsive-box">
        
        {/* Areas of Study Section */}
        <div className="job-series-screen area-of-study">
          <h1>Your Areas of Study</h1>
          <div className="job-series-screen fields-row">
            {areasOfStudy.map((study, index) => (
              <span key={index} className="job-series-screen field-item">{study}</span>
            ))}
          </div>
        </div>
        
        {/* Subjects Learned and Applied Section */}
        <div className="job-series-screen subjects-learned">
          <h1>Subjects Learned and Applied</h1>
          <div className="job-series-screen fields-row">
            {relevantCourses.map((course, index) => (
              <span key={index} className="job-series-screen field-item">{course}</span>
            ))}
          </div>
        </div>
        
        {/* Get Recommendations Button */}
        <button onClick={getRecommendations} className="job-series-screen recommendation-button" disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Get Job Series Recommendations'}
        </button>
        
        {isLoading && (
          <div className="job-series-screen loading-indicator">
            <Circles
              height="80"
              width="80"
              color="#4fa94d"
              ariaLabel="circles-loading"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
            />
          </div>
        )}
        
        {error && <p className="job-series-screen error-message">{error}</p>}
        
        {/* Recommendations Section */}
        {recommendations && (
    <div className="job-series-screen recommendations">
        <h1>Recommended Job Series</h1>
        <ul className="job-series-screen recommendations-list">
            {recommendations.map((recommendation, index) => {
                const isSelected = formData.selectedJobSeries?.code === recommendation.code;
                return (
                    <li key={index} className="job-series-screen recommendation-item">
                        <button
                            className={`job-series-screen recommendedItem ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectJobSeries(recommendation.code, recommendation.title)}
                        >
                            {recommendation.code} - {recommendation.title}
                        </button>
                    </li>
                );
            })}
        </ul>
    </div>
)}

      </div>
    </div>
  );
};

export default JobSeriesScreen;
